// import jsDoc types for IntelliSense
import '../../types/editor.d';
import '../../types/index.d';
import '../../types/button-markup.d';

import { getLocalizedContent, isNewSrp } from '../utils/helpers';
import { allowSelectedUpdatesToButtonClasses } from '../edit/locations/index';
import { log } from '../log';

const newSrp = isNewSrp();

/**
 * @param { Element } el
 * @param { AnyObject } attributes
 * @returns { Element }
 */
export const addAttributes = (el, attributes) => {
	if (typeof attributes === 'object') {
		Object.keys(attributes).forEach(attribute => {
			if (attribute) {
				el.setAttribute(attribute, attributes[attribute]);
			}
		});
	}
	return el;
};

/**
 * @param { Instance } instance
 * @param { Element } el
 * @param { InsertLocations } location
 * @returns { Element }
 */
export const applyButtonWrapperOverrides = (instance, el, location) => {
	el.classList.remove('hidden');

	// Set attributes on the element to make debugging easier.
	if (instance.init && instance.init.integrationId) {
		el.setAttribute('data-modified-by', instance.init.integrationId);
	}
	el.setAttribute('data-content-source', 'Web Integration API');

	if (instance.targetLocation) {
		el.setAttribute('data-content-intent', instance.targetLocation);
	}

	if (instance.destinationLocation) {
		el.setAttribute('data-content-destination', instance.destinationLocation);
	}

	if (location) {
		el.setAttribute('data-location', location);
	}

	return el;
};

/**
 *
 * @param { Instance } instance
 * @param { Element } element
 * @param { ButtonOverrides } options
 * @param { Boolean } vehicleCTA
 * @param { Boolean } includeWIAPIAttributes
 * @returns
 */
export const applyButtonOverrides = (
	instance,
	element,
	options,
	vehicleCTA = true,
	includeWIAPIAttributes = true
) => {
	const allowModifyClasses =
		!instance.destinationLocation ||
		!allowSelectedUpdatesToButtonClasses.includes(instance.destinationLocation);

	// For links in mobile menu, avoid cloning to preserve event listeners
	// This is crucial for mobile menu links that may be dynamically replaced
	const isInMobileMenu =
		element.closest('.ddc-mobile-header-nav') ||
		element.closest('[data-location~="primary-menu"]') ||
		element.closest('.mobile-menu') ||
		element.closest('.ddc-mobile-header-sub-nav') ||
		element.closest('#ddc-mobile-header-nav');

	// Use original element for mobile menu or when onclick override is provided, to preserve event listeners
	let el;
	if (isInMobileMenu || options.onclick) {
		el = element;
	} else {
		// Remove other event listeners by cloning the element when no onclick override
		el = element.parentNode ? element.cloneNode(true) : element;
		if (element.parentNode) {
			element.parentNode.replaceChild(el, element);
		}
	}

	const buttonText = getLocalizedContent(options.text);

	if (options.href) {
		el.href = options.href;

		// This is here for backwards compatability.
		// It was originally defined as 'src' and set to 'href'
		// which doesn't really make sense, but some integrations
		// still use this.
	} else if (options.src) {
		el.href = options.src;
	} else {
		// Ensure anchor is clickable even without a navigation target
		el.setAttribute('href', '#');
		el.removeAttribute('data-href');
	}

	// Allow inline style overrides to resolve edge case issues.
	if (options.style && typeof options.style === 'string') {
		el.style = options.style.trim();
	}

	if (vehicleCTA && allowModifyClasses) {
		el.classList.add('btn', 'btn-block');

		if (newSrp) {
			el.classList.add('btn-sm');
		}

		el.classList.remove('btn-default', 'btn-primary');
	}

	if (options.imgSrc) {
		el.innerHTML = '';

		let img = document.createElement('img');

		img.src = options.imgSrc;
		img.alt = options.imgAlt ? getLocalizedContent(options.imgAlt) : buttonText;

		if (options.imgClasses) {
			img.className = options.imgClasses;
		}

		img = addAttributes(img, options.imgAttributes);

		el.appendChild(img);
	} else if (buttonText) {
		el.innerHTML = buttonText;
		el.setAttribute('alt', buttonText);

		if (vehicleCTA && allowModifyClasses) {
			if (options.type === 'primary' || options.type === 'text') {
				el.classList.add(`btn-${options.type}`);
			} else {
				el.classList.add('btn-default');
			}
		}
	}

	if (options.onclick) {
		// Check if this element has already been processed
		if (el.dataset.wiapiProcessed) {
			return el;
		}
		// Add a wrapped click handler that preserves other handlers
		const wrappedOnclick = function wrappedOnclick(event) {
			// Prevent default link behavior since we're handling the click
			event.preventDefault();
			try {
				return options.onclick.call(this, event);
			} catch (error) {
				log('[BUTTONMARKUP] Error in onclick handler:', error);
				throw error;
			}
		};
		// Use addEventListener to avoid overwriting existing listeners
		el.addEventListener('click', wrappedOnclick);
		// Mark element as processed to avoid duplicate handlers
		el.dataset.wiapiProcessed = 'true';
	}

	if (options.target) {
		el.setAttribute('target', options.target);
	}

	// Remove attributes which should not be set by default.
	// The attributes can be re-added in the next step.
	['data-width', 'data-dialog-class'].forEach(attributeName => {
		el.removeAttribute(attributeName);
	});

	el = addAttributes(el, options.attributes);

	// Remove these classes as they should be off by default
	// and can be re-added by the integration in the next code block.
	['dialog', 'hide', 'hidden'].forEach(className => {
		el.classList.remove(className);
	});

	// The above classes are re-added here in case they are missing,
	// and newly specified classes are added.
	if (options.classes && options.classes.trim() !== '') {
		options.classes
			.trim()
			.split(' ')
			.forEach(className => {
				el.classList.add(className);
			});
	}

	if (includeWIAPIAttributes) {
		el = applyButtonWrapperOverrides(instance, el);
	}

	// Popover support
	if (options.popover) {
		const popoverOptions = {
			toggle: 'popover',
			animation: options.popover.animation || true,
			container: options.popover.container || 'body',
			'delay-show': options.popover.delayShow || 0,
			'delay-hide': options.popover.delayHide || 0,
			title: getLocalizedContent(options.popover.title) || '',
			content: getLocalizedContent(options.popover.content) || '',
			html: options.popover.html || '',
			placement: options.popover.placement || 'left',
			trigger: options.popover.trigger || 'hover'
		};

		Object.keys(popoverOptions).forEach(attribute => {
			el.setAttribute(`data-${attribute}`, popoverOptions[attribute]);
		});
	}

	return el;
};
